import React, { useState, useEffect } from 'react';
import { superAdminApi } from '../../services/superAdminApi';
import ApprovalModal from './ApprovalModal';
import RejectionModal from './RejectionModal';
import RequestDetailsModal from './RequestDetailsModal';

const SignupRequestsTable = ({ onRequestProcessed }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    status: 'pending',
    search: '',
    per_page: 15
  });
  
  // Modal states
  const [approvalModal, setApprovalModal] = useState({ show: false, request: null });
  const [rejectionModal, setRejectionModal] = useState({ show: false, request: null });
  const [detailsModal, setDetailsModal] = useState({ show: false, request: null });

  useEffect(() => {
    fetchRequests();
  }, [filters]);

  const fetchRequests = async (page = 1) => {
    try {
      setLoading(true);
      const params = { ...filters, page };
      const response = await superAdminApi.getSignupRequests(params);
      
      // Handle both response structures
      if (response.data.data) {
        setRequests(response.data.data.signup_requests || []);
        setPagination(response.data.data.pagination || null);
      } else {
        setRequests(response.data.signup_requests || []);
        setPagination(response.data.pagination || null);
      }
    } catch (error) {
      console.error('Failed to fetch signup requests:', error);
      setRequests([]); // Ensure requests is always an array
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (request) => {
    setApprovalModal({ show: true, request });
  };

  const handleReject = (request) => {
    setRejectionModal({ show: true, request });
  };

  const handleViewDetails = (request) => {
    setDetailsModal({ show: true, request });
  };

  const handleApprovalComplete = () => {
    setApprovalModal({ show: false, request: null });
    fetchRequests();
    onRequestProcessed && onRequestProcessed();
  };

  const handleRejectionComplete = () => {
    setRejectionModal({ show: false, request: null });
    fetchRequests();
    onRequestProcessed && onRequestProcessed();
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', icon: 'clock', text: 'Pending' },
      approved: { class: 'status-approved', icon: 'check', text: 'Approved' },
      rejected: { class: 'status-rejected', icon: 'times', text: 'Rejected' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`status-badge ${badge.class}`}>
        <i className={`fas fa-${badge.icon}`}></i>
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="signup-requests-table">
      {/* Filters */}
      <div className="table-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="filter-select"
            >
              <option value="all">All Requests</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Organization name, contact person, or email..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Show:</label>
            <select
              value={filters.per_page}
              onChange={(e) => setFilters({ ...filters, per_page: parseInt(e.target.value) })}
              className="filter-select"
            >
              <option value={10}>10 per page</option>
              <option value={15}>15 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="table-loading">
            <i className="fas fa-spinner fa-spin"></i>
            Loading requests...
          </div>
        ) : (
          <table className="requests-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Contact Person</th>
                <th>Email</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    No signup requests found
                  </td>
                </tr>
              ) : (
                requests.map(request => (
                  <tr key={request.id} className="request-row">
                    <td className="org-name">
                      <div className="org-info">
                        <strong>{request.organization_name}</strong>
                        {request.website && (
                          <div className="org-website">
                            <a href={request.website} target="_blank" rel="noopener noreferrer">
                              <i className="fas fa-external-link-alt"></i>
                              {request.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{request.contact_person}</td>
                    <td>
                      <a href={`mailto:${request.email}`}>{request.email}</a>
                      {request.phone && (
                        <div className="contact-phone">
                          <i className="fas fa-phone"></i>
                          {request.phone}
                        </div>
                      )}
                    </td>
                    <td>{getStatusBadge(request.status)}</td>
                    <td className="date-cell">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleViewDetails(request)}
                          className="btn-secondary btn-sm"
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(request)}
                              className="btn-success btn-sm"
                              title="Approve Request"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              onClick={() => handleReject(request)}
                              className="btn-danger btn-sm"
                              title="Reject Request"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.last_page > 1 && (
        <div className="table-pagination">
          <div className="pagination-info">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
            {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
            {pagination.total} requests
          </div>
          
          <div className="pagination-controls">
            <button
              onClick={() => fetchRequests(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="btn-secondary btn-sm"
            >
              <i className="fas fa-chevron-left"></i>
              Previous
            </button>
            
            <span className="page-info">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            
            <button
              onClick={() => fetchRequests(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="btn-secondary btn-sm"
            >
              Next
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {approvalModal.show && (
        <ApprovalModal
          request={approvalModal.request}
          onClose={() => setApprovalModal({ show: false, request: null })}
          onComplete={handleApprovalComplete}
        />
      )}

      {rejectionModal.show && (
        <RejectionModal
          request={rejectionModal.request}
          onClose={() => setRejectionModal({ show: false, request: null })}
          onComplete={handleRejectionComplete}
        />
      )}

      {detailsModal.show && (
        <RequestDetailsModal
          request={detailsModal.request}
          onClose={() => setDetailsModal({ show: false, request: null })}
        />
      )}
    </div>
  );
};

export default SignupRequestsTable;